/**
 * Supabase Client Configuration
 * Browser-side client for interacting with Supabase
 *
 * Uses createBrowserClient from @supabase/ssr to store sessions in cookies
 * instead of localStorage. This ensures the browser-side client stays in sync
 * with the server-side middleware (middleware.ts) which also uses cookies.
 */

import { createBrowserClient } from '@supabase/ssr';
import type { Database } from './database.types';
import { browserCookieSecureDefault } from '@/lib/auth/secure-cookie-options';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

// Only throw error in browser context, not during build
if (typeof window !== 'undefined' && (!supabaseUrl || !supabaseAnonKey)) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createBrowserClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    // `@supabase/ssr` writes the session cookie from JS with its own
    // DEFAULT_COOKIE_OPTIONS, which carries no `secure` flag — so a
    // client-side sign-in produced a non-Secure `sb-*-auth-token` even on
    // HTTPS deployments (Aikido #348 / #349). Server-side writes are
    // covered separately by `hardenCookieOptions`. `sameSite: 'lax'`
    // matches the server default and the SDK's own.
    cookieOptions: {
      secure: browserCookieSecureDefault(),
      sameSite: 'lax',
    },
  }
);

/**
 * gotrue-js serialises every `supabase.auth.*` call through a single
 * Web-Lock named `lock:sb-<ref>-auth-token`. When two of those calls
 * overlap during page mount (e.g. AuthProvider's `getSession()` racing a
 * page-level `setSession()`/`getUser()` on `/auth/accept-invite` or
 * `/auth/callback-client`), the lower-priority operation rejects with
 *
 *   "Lock 'lock:sb-<ref>-auth-token' was released because another
 *    request stole it"
 *
 * The condition is transient — the lock is free once the stealing call
 * finishes — so a single retry after a tiny delay is sufficient. Use
 * this wrapper around any client-side auth call that might race the
 * AuthProvider on first render.
 */
export function isAuthLockStolenError(err: unknown): boolean {
  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : (err as { message?: string } | null)?.message ?? '';
  return /released because another request stole it/i.test(message);
}

export async function withAuthLockRetry<T>(
  operation: () => Promise<T>,
  { retryDelayMs = 50 }: { retryDelayMs?: number } = {},
): Promise<T> {
  try {
    return await operation();
  } catch (err) {
    if (!isAuthLockStolenError(err)) throw err;
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    return await operation();
  }
}

